import React, { useCallback, useContext, useState } from 'react';
import { CloseCircleOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import {
  LinkResponse,
  PartyInvolved,
  TicketQueryResult,
} from '@totem/components/ticketing/ticketing';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import IBDIcon from '@totem/styles/Icon';
import { formatDate, isNotNull } from '@totem/utilities/common';
import { TICKET_ENDPOINT } from '@totem/utilities/endpoints';

import '../../ticketing.css';

type Props = {
  ticket: TicketQueryResult;
  onEndPartyInvolvement: (partyInvolved: PartyInvolved) => void;
};

const PartiesInvolvedTable = ({ ticket, onEndPartyInvolvement }: Props) => {
  const { userProfile, getApiToken, showTooltips } =
    useContext(UserProfileContext);
  const [isSending, setIsSending] = useState(false);
  const [api, contextHolder] = notification.useNotification();

  const sendGetToken = useCallback(
    async (partyInvolvedId: string) => {
      if (isSending) {
        return;
      }
      fetch(
        `${TICKET_ENDPOINT}/${ticket.ticket.id}/partyInvolved/${partyInvolvedId}/link`,
        {
          method: 'GET',
          headers: new Headers({
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getApiToken()}`,
          }),
        },
      )
        .then((res) => res.json())
        .then((response: LinkResponse) => {
          if (isNotNull(response) && response.token !== '') {
            navigator.clipboard.writeText(response.token).then(() => {
              api.success({
                title: 'Link Copied',
                description:
                  'The link to the survey should be copied to your clipboard.',
              });
            });
          }
        })
        .then(() => {
          setIsSending(false);
        });

      setIsSending(true);
    },
    [isSending],
  );

  const handleGetLink = (partyInvolved: PartyInvolved) => {
    sendGetToken(partyInvolved.id);
  };

  const columns: ColumnProps<PartyInvolved>[] = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.type === compB.type ? 0 : compA.type > compB.type ? 1 : -1,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.email === compB.email ? 0 : compA.email > compB.email ? 1 : -1,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.phone === compB.phone ? 0 : compA.phone > compB.phone ? 1 : -1,
    },
    {
      title: 'Involvement Start',
      dataIndex: 'involvementStart',
      key: 'involvementStart',
      render: (_, partyInvolved: PartyInvolved) =>
        formatDate(partyInvolved.involvementStart),
      sorter: (compA, compB) =>
        compA.involvementStart === compB.involvementStart
          ? 0
          : compA.involvementStart > compB.involvementStart
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Involvement End',
      dataIndex: 'involvementEnd',
      key: 'involvementEnd',
      render: (_, partyInvolved: PartyInvolved) =>
        formatDate(partyInvolved.involvementEnd),
      sorter: (compA, compB) =>
        compA.involvementEnd === compB.involvementEnd
          ? 0
          : compA.involvementEnd > compB.involvementEnd
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      sorter: (compA, compB) =>
        compA.status === compB.status
          ? 0
          : compA.status > compB.status
            ? 1
            : -1,
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
    },
    {
      title: 'Actions',
      width: 30,
      dataIndex: 'additionalOptions',
      key: 'additionalOptions',
      render: (_, partyInvolved: PartyInvolved) =>
        partyInvolved.status === 'ACTIVE' && (
          <div className="center-table-cell">
            <Button
              title={'Remove Involvement'}
              onClick={() => onEndPartyInvolvement(partyInvolved)}
              icon={<CloseCircleOutlined />}
            />
            {permissions.internalSystemAdministrator.hasPermissions(
              userProfile,
            ) &&
              showTooltips && (
                <Button
                  style={{ marginLeft: '.5rem' }}
                  title={'Get Link'}
                  onClick={() => handleGetLink(partyInvolved)}
                  icon={<IBDIcon name={'Link'} />}
                />
              )}
          </div>
        ),
    },
  ];

  const getPartiesInvolved = () => {
    if (ticket !== null) {
      if (ticket.ticket !== null) {
        return ticket.ticket.partiesInvolved;
      }
    }
    return [];
  };

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={getPartiesInvolved()}
        loading={false}
        rowKey="id"
      />
      {contextHolder}
    </>
  );
};

export default PartiesInvolvedTable;
